// Mailing components exports - All German
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { ChevronUp, ChevronDown, MoreVertical, Edit, Trash2, Eye, RefreshCw } from 'lucide-react';

export function SortIcon({ field, sort }) {
  if (sort.field !== field) return null;
  return sort.dir === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />;
}

// Table for Vitalis contacts (formerly "Base Centre")
export function ContactsCentreTable({ contacts, selected, onSelect, onSelectAll, sort, onSort, tags, onEdit, onDelete }) {
  return (
    <div className="border rounded-lg overflow-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">
              <Checkbox 
                checked={selected.length === contacts.length && contacts.length > 0} 
                onCheckedChange={onSelectAll} 
              />
            </TableHead>
            <TableHead>Anrede</TableHead>
            <TableHead className="cursor-pointer" onClick={() => onSort('nachname')}>
              Nachname <SortIcon field="nachname" sort={sort} />
            </TableHead>
            <TableHead className="cursor-pointer" onClick={() => onSort('vorname')}>
              Vorname <SortIcon field="vorname" sort={sort} />
            </TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Telefon</TableHead>
            <TableHead>Klassierung</TableHead>
            <TableHead>Tags</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="w-12"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {contacts.map(c => (
            <TableRow key={c.id} className={c.disabled ? 'opacity-50' : ''}>
              <TableCell>
                <Checkbox 
                  checked={selected.includes(c.id)} 
                  onCheckedChange={ch => onSelect(c.id, ch)} 
                />
              </TableCell>
              <TableCell className="text-sm">{c.anrede || c.civilite || '-'}</TableCell>
              <TableCell 
                className="font-medium text-primary cursor-pointer hover:underline" 
                onClick={() => onEdit(c)}
                data-testid={`contact-centre-name-${c.id}`}
              >
                {c.nachname || c.nom}
              </TableCell>
              <TableCell 
                className="cursor-pointer hover:underline" 
                onClick={() => onEdit(c)}
              >
                {c.vorname || c.prenom}
              </TableCell>
              <TableCell>{c.email}</TableCell>
              <TableCell>{c.telefon || c.telephone || '-'}</TableCell>
              <TableCell className="text-sm">{c.klassierung || c.classement_tennis || '-'}</TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {c.tags?.map(t => (
                    <Badge 
                      key={t} 
                      style={{ backgroundColor: tags.find(x => x.name === t)?.color || '#888' }} 
                      className="text-white text-xs"
                    >
                      {t}
                    </Badge>
                  ))}
                </div>
              </TableCell>
              <TableCell>
                {c.disabled ? (
                  <Badge variant="secondary">Deaktiviert</Badge>
                ) : (
                  <Badge className="bg-green-500">Aktiv</Badge>
                )}
              </TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon"><MoreVertical className="h-4 w-4" /></Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onEdit(c)}>
                      <Edit className="h-4 w-4 mr-2" />Bearbeiten
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onDelete(c.id)} className="text-red-600">
                      <Trash2 className="h-4 w-4 mr-2" />Löschen
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
          {contacts.length === 0 && (
            <TableRow>
              <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                Keine Kontakte
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

// Table for Swiss Tennis contacts
export function ContactsProjetsTable({ contacts, selected, onSelect, onSelectAll, sort, onSort, onEdit, onDelete }) {
  return (
    <div className="border rounded-lg overflow-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">
              <Checkbox 
                checked={selected.length === contacts.length && contacts.length > 0} 
                onCheckedChange={onSelectAll} 
              />
            </TableHead>
            <TableHead>Anrede</TableHead>
            <TableHead className="cursor-pointer" onClick={() => onSort('nachname')}>
              Nachname <SortIcon field="nachname" sort={sort} />
            </TableHead>
            <TableHead className="cursor-pointer" onClick={() => onSort('vorname')}>
              Vorname <SortIcon field="vorname" sort={sort} />
            </TableHead>
            <TableHead>Geschlecht</TableHead>
            <TableHead>Sprache</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Klassierung</TableHead>
            <TableHead>Alterskategorie</TableHead>
            <TableHead>Regionalverband</TableHead>
            <TableHead className="w-12"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {contacts.map(c => (
            <TableRow key={c.id}>
              <TableCell>
                <Checkbox 
                  checked={selected.includes(c.id)} 
                  onCheckedChange={ch => onSelect(c.id, ch)} 
                />
              </TableCell>
              <TableCell className="text-sm">{c.anrede || '-'}</TableCell>
              <TableCell 
                className="font-medium text-primary cursor-pointer hover:underline" 
                onClick={() => onEdit(c)}
                data-testid={`contact-projets-name-${c.id}`}
              >
                {c.nachname || c.nom}
              </TableCell>
              <TableCell 
                className="cursor-pointer hover:underline" 
                onClick={() => onEdit(c)}
              >
                {c.vorname || c.prenom}
              </TableCell>
              <TableCell className="text-sm">{c.geschlecht || '-'}</TableCell>
              <TableCell className="text-sm">{c.sprache || '-'}</TableCell>
              <TableCell>{c.email}</TableCell>
              <TableCell className="text-sm">{c.klassierung || '-'}</TableCell>
              <TableCell className="text-sm">{c.alterskategorie || '-'}</TableCell>
              <TableCell className="text-sm">{c.regionalverband || '-'}</TableCell>
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon"><MoreVertical className="h-4 w-4" /></Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onEdit(c)}>
                      <Edit className="h-4 w-4 mr-2" />Bearbeiten
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onDelete(c.id)} className="text-red-600">
                      <Trash2 className="h-4 w-4 mr-2" />Löschen
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
          {contacts.length === 0 && (
            <TableRow>
              <TableCell colSpan={11} className="text-center text-muted-foreground py-8">
                Keine Kontakte
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}


// Table for Campaigns history
export function CampaignsTable({ campaigns, onView, onResume }) {
  return (
    <div className="border rounded-lg overflow-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Datum</TableHead>
            <TableHead>Absender</TableHead>
            <TableHead>Betreff</TableHead>
            <TableHead>Empfänger</TableHead>
            <TableHead>Gesendet</TableHead>
            <TableHead>Fehler</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="w-12"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {campaigns.map(c => (
            <TableRow key={c.id}>
              <TableCell className="text-sm">{new Date(c.started_at).toLocaleDateString('de-DE')}</TableCell>
              <TableCell className="text-sm">{c.sender_name || c.sender_email}</TableCell>
              <TableCell className="font-medium">{c.subject}</TableCell>
              <TableCell>{c.total_recipients}</TableCell>
              <TableCell className="text-green-600">{c.sent_count}</TableCell>
              <TableCell className="text-red-600">{c.failed_count}</TableCell>
              <TableCell>
                {c.status === 'completed' ? (
                  <Badge className="bg-green-500">Abgeschlossen</Badge>
                ) : c.status === 'sending' ? (
                  <Badge className="bg-yellow-500">Wird gesendet</Badge>
                ) : (
                  <Badge variant="secondary">{c.status}</Badge>
                )}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-1">
                  {c.sent_count < c.total_recipients && (
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Envoi à reprendre"
                      onClick={() => onResume && onResume(c)}
                      data-testid={`resume-campaign-${c.id}`}
                    >
                      <RefreshCw className="h-4 w-4 text-orange-600" />
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" onClick={() => onView(c.id)} data-testid={`view-campaign-${c.id}`}>
                    <Eye className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
          {campaigns.length === 0 && (
            <TableRow>
              <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                Keine Kampagnen
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
